/**
 * useFinanceExtended hooks — Issue #329.
 *
 * Typed hooks for invoice / installment / adjustment / summary endpoints,
 * built on the generated @jinzhuan/api-client types.
 */
import { useCallback, useState } from 'react';
import { createApiClient, ApiError as ApiClientError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

// ─── Generated DTO aliases ────────────────────────────────────────────────────

export type InvoiceSummaryDto = ApiComponents['schemas']['InvoiceSummaryDto'];
export type InvoiceDetailDto = ApiComponents['schemas']['InvoiceDetailDto'];
export type InvoiceListResponseDto =
  ApiComponents['schemas']['InvoiceListResponseDto'];
export type CreateInvoiceDto = ApiComponents['schemas']['CreateInvoiceDto'];
export type UpdateInvoiceDto = ApiComponents['schemas']['UpdateInvoiceDto'];
export type SubmitInvoiceDto = ApiComponents['schemas']['SubmitInvoiceDto'];
export type IssueInvoiceDto = ApiComponents['schemas']['IssueInvoiceDto'];
export type RejectInvoiceDto = ApiComponents['schemas']['RejectInvoiceDto'];
export type VoidInvoiceDto = ApiComponents['schemas']['VoidInvoiceDto'];

export type InstallmentPlanSummaryDto =
  ApiComponents['schemas']['InstallmentPlanSummaryDto'];
export type InstallmentPlanDetailDto =
  ApiComponents['schemas']['InstallmentPlanDetailDto'];
export type InstallmentPlanListResponseDto =
  ApiComponents['schemas']['InstallmentPlanListResponseDto'];
export type InstallmentItemDto =
  ApiComponents['schemas']['InstallmentItemDto'];
export type InstallmentItemListResponseDto =
  ApiComponents['schemas']['InstallmentItemListResponseDto'];
export type CreateInstallmentPlanDto =
  ApiComponents['schemas']['CreateInstallmentPlanDto'];
export type UpdateInstallmentPlanDto =
  ApiComponents['schemas']['UpdateInstallmentPlanDto'];
export type ActivateInstallmentPlanDto =
  ApiComponents['schemas']['ActivateInstallmentPlanDto'];
export type CancelInstallmentPlanDto =
  ApiComponents['schemas']['CancelInstallmentPlanDto'];
export type CompleteInstallmentPlanDto =
  ApiComponents['schemas']['CompleteInstallmentPlanDto'];
export type FollowUpInstallmentItemDto =
  ApiComponents['schemas']['FollowUpInstallmentItemDto'];

export type FinanceAdjustmentSummaryDto =
  ApiComponents['schemas']['FinanceAdjustmentSummaryDto'];
export type FinanceAdjustmentDetailDto =
  ApiComponents['schemas']['FinanceAdjustmentDetailDto'];
export type FinanceAdjustmentListResponseDto =
  ApiComponents['schemas']['FinanceAdjustmentListResponseDto'];
export type CreateFinanceAdjustmentDto =
  ApiComponents['schemas']['CreateFinanceAdjustmentDto'];
export type UpdateFinanceAdjustmentDto =
  ApiComponents['schemas']['UpdateFinanceAdjustmentDto'];
export type SubmitFinanceAdjustmentDto =
  ApiComponents['schemas']['SubmitFinanceAdjustmentDto'];
export type ReviewFinanceAdjustmentDto =
  ApiComponents['schemas']['ReviewFinanceAdjustmentDto'];
export type ExecuteFinanceAdjustmentDto =
  ApiComponents['schemas']['ExecuteFinanceAdjustmentDto'];
export type CancelFinanceAdjustmentDto =
  ApiComponents['schemas']['CancelFinanceAdjustmentDto'];
export type FinanceSummaryDto = ApiComponents['schemas']['FinanceSummaryDto'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown, status?: number): ApiError {
  const effective =
    status ?? (err instanceof ApiClientError ? err.status : undefined);
  if (effective === 401) {
    return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED' };
  }
  if (effective === 409) {
    return { message: '数据已被其他用户修改，请刷新页面后重试', code: 'CONFLICT' };
  }
  if (effective === 403) {
    return { message: '没有权限执行该操作', code: 'FORBIDDEN' };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

// ─── Invoices ─────────────────────────────────────────────────────────────────

export function useInvoices() {
  const [data, setData] = useState<InvoiceListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(
    async (filter?: {
      status?: string;
      customerId?: string;
      orderId?: string;
      contractId?: string;
      search?: string;
      page?: number;
      limit?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, string | number> = {};
        if (filter?.status) query.status = filter.status;
        if (filter?.customerId) query.customerId = filter.customerId;
        if (filter?.orderId) query.orderId = filter.orderId;
        if (filter?.contractId) query.contractId = filter.contractId;
        if (filter?.search) query.search = filter.search;
        if (filter?.page) query.page = filter.page;
        if (filter?.limit) query.limit = filter.limit;
        const { data: ok, error: apiErr, response } = await apiClient.GET(
          '/api/v1/finance/invoices',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setData(ok ?? null);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, loading, error, fetchList };
}

export function useInvoiceActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const create = useCallback(async (dto: CreateInvoiceDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/invoices',
        { body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const submit = useCallback(async (id: string, dto: SubmitInvoiceDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/invoices/{id}/submit',
        { params: { path: { id } }, body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const issue = useCallback(async (id: string, dto: IssueInvoiceDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/invoices/{id}/issue',
        { params: { path: { id } }, body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const reject = useCallback(async (id: string, dto: RejectInvoiceDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/invoices/{id}/reject',
        { params: { path: { id } }, body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const voidInvoice = useCallback(async (id: string, dto: VoidInvoiceDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/invoices/{id}/void',
        { params: { path: { id } }, body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, create, submit, issue, reject, voidInvoice };
}

// ─── Installments ────────────────────────────────────────────────────────────

export function useInstallmentItems() {
  const [data, setData] = useState<InstallmentItemListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(
    async (filter?: {
      planId?: string;
      customerId?: string;
      status?: string;
      overdueOnly?: boolean;
      upcomingOnly?: boolean;
      upcomingWindowDays?: number;
      page?: number;
      limit?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, string | number | boolean> = {};
        if (filter?.planId) query.planId = filter.planId;
        if (filter?.customerId) query.customerId = filter.customerId;
        if (filter?.status) query.status = filter.status;
        if (filter?.overdueOnly) query.overdueOnly = true;
        if (filter?.upcomingOnly) query.upcomingOnly = true;
        if (filter?.upcomingWindowDays)
          query.upcomingWindowDays = filter.upcomingWindowDays;
        if (filter?.page) query.page = filter.page;
        if (filter?.limit) query.limit = filter.limit;
        const { data: ok, error: apiErr, response } = await apiClient.GET(
          '/api/v1/finance/installment-items',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setData(ok ?? null);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, loading, error, fetchList };
}

export function useInstallmentPlans() {
  const [data, setData] = useState<InstallmentPlanListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(
    async (filter?: {
      status?: string;
      customerId?: string;
      orderId?: string;
      contractId?: string;
      search?: string;
      page?: number;
      limit?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, string | number> = {};
        if (filter?.status) query.status = filter.status;
        if (filter?.customerId) query.customerId = filter.customerId;
        if (filter?.orderId) query.orderId = filter.orderId;
        if (filter?.contractId) query.contractId = filter.contractId;
        if (filter?.search) query.search = filter.search;
        if (filter?.page) query.page = filter.page;
        if (filter?.limit) query.limit = filter.limit;
        const { data: ok, error: apiErr, response } = await apiClient.GET(
          '/api/v1/finance/installment-plans',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setData(ok ?? null);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, loading, error, fetchList };
}

export function useInstallmentActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const followUp = useCallback(
    async (id: string, dto: FollowUpInstallmentItemDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/installment-items/{id}/follow-up',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createPlan = useCallback(async (dto: CreateInstallmentPlanDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/installment-plans',
        { body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const activatePlan = useCallback(
    async (id: string, dto: ActivateInstallmentPlanDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/installment-plans/{id}/activate',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, followUp, createPlan, activatePlan };
}

// ─── Finance adjustments ─────────────────────────────────────────────────────

export function useFinanceAdjustments() {
  const [data, setData] = useState<FinanceAdjustmentListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(
    async (filter?: {
      status?: string;
      adjustmentType?: string;
      customerId?: string;
      orderId?: string;
      contractId?: string;
      search?: string;
      page?: number;
      limit?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, string | number> = {};
        if (filter?.status) query.status = filter.status;
        if (filter?.adjustmentType) query.adjustmentType = filter.adjustmentType;
        if (filter?.customerId) query.customerId = filter.customerId;
        if (filter?.orderId) query.orderId = filter.orderId;
        if (filter?.contractId) query.contractId = filter.contractId;
        if (filter?.search) query.search = filter.search;
        if (filter?.page) query.page = filter.page;
        if (filter?.limit) query.limit = filter.limit;
        const { data: ok, error: apiErr, response } = await apiClient.GET(
          '/api/v1/finance/adjustments',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setData(ok ?? null);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, loading, error, fetchList };
}

export function useFinanceAdjustmentDetail(id: string | null) {
  const [data, setData] = useState<FinanceAdjustmentDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { data: ok, error: apiErr, response } = await apiClient.GET(
        '/api/v1/finance/adjustments/{id}',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setData(ok ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  return { data, loading, error, refresh };
}

export function useFinanceAdjustmentActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const create = useCallback(async (dto: CreateFinanceAdjustmentDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/adjustments',
        { body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const submit = useCallback(async (id: string, dto: SubmitFinanceAdjustmentDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/adjustments/{id}/submit',
        { params: { path: { id } }, body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const review = useCallback(async (id: string, dto: ReviewFinanceAdjustmentDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/adjustments/{id}/review',
        { params: { path: { id } }, body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const execute = useCallback(async (id: string, dto: ExecuteFinanceAdjustmentDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/adjustments/{id}/execute',
        { params: { path: { id } }, body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const cancel = useCallback(async (id: string, dto: CancelFinanceAdjustmentDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/adjustments/{id}/cancel',
        { params: { path: { id } }, body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, create, submit, review, execute, cancel };
}

// ─── Finance summary ─────────────────────────────────────────────────────────

export function useFinanceSummary() {
  const [data, setData] = useState<FinanceSummaryDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchSummary = useCallback(
    async (query: { customerId?: string; orderId?: string; contractId?: string }) => {
      setLoading(true);
      setError(null);
      try {
        const { data: ok, error: apiErr, response } = await apiClient.GET(
          '/api/v1/finance/summary',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setData(ok ?? null);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, loading, error, fetchSummary };
}
